import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../lib/i18n';
import type { Role } from '@shared/types';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: boolean;
  lastLoginAt: number | null;
}

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { t } = useI18n();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ data: UserRow[] }>('/admin/users').then((r) => r.data),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserRow> }) =>
      api.patch(`/admin/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">{t('common.loading')}</div>;

  return (
    <div>
      <h1 className="mb-6 font-heading text-3xl font-bold text-dark">{t('users.title')}</h1>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">{t('users.name')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('common.email')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('users.role')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('users.access')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-4 py-3">
                    {u.name}
                    {isMe && <span className="ml-2 text-xs text-gray-400">{t('users.me')}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      className="input w-32"
                      value={u.role}
                      disabled={isMe}
                      onChange={(e) =>
                        patch.mutate({ id: u.id, body: { role: e.target.value as Role } })
                      }
                    >
                      <option value="user">{t('users.roleUser')}</option>
                      <option value="admin">{t('users.roleAdmin')}</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={u.isActive}
                        disabled={isMe}
                        onChange={(e) =>
                          patch.mutate({ id: u.id, body: { isActive: e.target.checked } })
                        }
                      />
                      <span className="text-gray-600">{u.isActive ? t('users.active') : t('users.blocked')}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {t('users.hint')}
      </p>
    </div>
  );
}
